package com.iregained.site.components.sections.promo.sections

import androidx.compose.runtime.*
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Pop-In
@Composable
private fun Modifier.staggeredPopIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun PromoValueSection() {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    var isVisible by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
            .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Wrap the content in a Div to correctly handle toAttrs and IntersectionObserver
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true
                                    obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element)
                        onDispose { observer.disconnect() }
                    }
                }) {
            SimpleGrid(
                numColumns(1, md = 3), modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(40.px)
            ) {
                ValueCard(
                    icon = Res.Icons.LIVE_DEMO,
                    title = "Live Virtual Demo",
                    desc = "Get a comprehensive, guided walkthrough of the MyHand® hardware and software with our clinical experts.",
                    modifier = Modifier.staggeredPopIn(isVisible, 100)
                )
                ValueCard(
                    icon = Res.Icons.CHART,
                    title = "Clinical Efficacy",
                    desc = "Drive better patient outcomes through targeted neuroplasticity protocols.",
                    modifier = Modifier.staggeredPopIn(isVisible, 250)
                )
                ValueCard(
                    icon = Res.Icons.TRAINING,
                    title = "Turnkey Setup",
                    desc = "We provide adequate staff training and fast deployment out of the box.",
                    modifier = Modifier.staggeredPopIn(isVisible, 400)
                )
            }
        }
    }
}

@Composable
fun ValueCard(icon: String, title: String, desc: String, modifier: Modifier = Modifier) {
    // Independent hover state for each card
    var isHovered by remember { mutableStateOf(false) }

    Column(
        modifier = modifier.fillMaxWidth()
        // Lift and scale the whole card on hover
        .scale(if (isHovered) 1.02f else 1f).translateY(if (isHovered) (-8).px else 0.px)
        .transition(Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut))
        .onMouseEnter { isHovered = true }.onMouseLeave { isHovered = false }
        .padding(16.px), // Add a little padding so the hover effect feels contained
        horizontalAlignment = Alignment.CenterHorizontally) {
        Image(
            src = icon, modifier = Modifier.size(64.px).margin(bottom = 20.px)
                // Add an extra little bounce to the icon itself
                .scale(if (isHovered) 1.1f else 1f)
                .transition(Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut))
        )
        SpanText(
            text = title, modifier = Modifier.fontSize(22.px).fontWeight(FontWeight.Bold).margin(bottom = 12.px)
        )
        SpanText(
            text = desc,
            modifier = Modifier.fontSize(16.px).textAlign(TextAlign.Center).color(Colors.DimGray).lineHeight(1.5)
        )
    }
}